import Backbone from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';

import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {SummaryMapState} from 'molbio/maps/MapStates';

import * as vo from 'components/VisibilityOptions';
import {ALIGNMENT_SCROLLING_PERF_INTERVAL} from 'constants/AlignmentConstants';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import ReactWrapperView from 'molbio/maps/linear/ReactWrapperView';
import SequenceAxisView from 'molbio/maps/linear/SequenceAxisView';
import LineView from 'molbio/maps/linear/LineView';
import AlignmentCommentsView from 'molbio/maps/linear/AlignmentCommentsView';
import TooltipBarGraph from 'molbio/maps/linear/TooltipBarGraph';
import * as Perf from 'performance/Perf';
import {getAminoAcidMatrix} from 'molbio/proteins/alignments/ProteinAlignmentStatsUtil';
import {getIdentityValues} from 'molbio/proteins/alignments/ProteinAlignmentUtil';
import movingAverage from 'util/movingAverage';
import {debounce} from 'ts-debounce';
import {MouseWheelEvent} from 'util/Mouse';

/**
 * A component similar to SliceView that can render SliceView components for multiple sequences in an
 * alignment. If a component is named in `y`, that component will be rendered for each sequence associated
 * with the SummarySliceView. Two "global" components are also available: `sequenceAxis` and
 * `alignmentComments`. A tabstate may be either a (Sequence)AlignmentTabState or a ProteinAlignmentTabState,
 * so data should be interpeted through tabstate methods.
 */
export default class SummarySliceView extends Backbone.View {
  static initClass() {
    this.prototype.tagName = 'svg';
    this.prototype.className = 'slice';
  }

  events() {
    return {
      mousedown: 'updateRange',
      mousemove: 'updateRange',
      mouseup: 'updateRange',
      dblclick: 'updateRange',
    };
  }

  triggerHeightChange(height) {
    // TODO: Investigate if protein alignments need something like this.
    if (this.model instanceof SummaryMapState) {
      this.model.set('sliceHeight', height);
    }
  }

  initialize(options) {
    this.performanceId = Perf.start(
      'drawAlignmentSummarySlice',
      {enabled: false},
      {
        interval: ALIGNMENT_SCROLLING_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
      },
      {}
    );
    this.mode = 'show';
    this.top = options.top || false;
    this.marginBottom = options.marginBottom || 0;
    this.mapType = options.mapType;
    this.tabstate = options.tabstate;
    this.blinkCursor = debounce(this.blinkCursor, 100);
    this.slice = options.slice;
    this.range = this.tabstate.get('range');
    this.y = options.y || ['line'];

    // Analogous to SliceView, we have methods keyed by component name that make it easy to render
    // a vertical list of these component by specifying an array of the component names.
    this.rowComponentMethods = {
      line: (mapstate) =>
        new LineView({
          model: mapstate,
          tabstate: this.tabstate,
          slice: this.slice,
          visibilityOptions: this.tabstate.getSummaryMapVisibilityOptions(),
          lineLayout: this.tabstate.getSummaryMapLineLayout(),
          parent: this,
        }),
    };
    this.componentMethods = {
      summaryIdentityGraph: () =>
        new ReactWrapperView({
          className: 'summaryIdentityGraph',
          setting: vo.SUMMARY_IDENTITY_GRAPH.name,
          model: this.tabstate,
          visibilityOptions: this.tabstate.get('visibilityOptions'),
          polymerLayoutName: 'summaryIdentityGraph',
          boxHeight: (layout) => layout.height + layout.vMargin,
          renderReact: (layout) => {
            const identityValues = getIdentityValues(
              getAminoAcidMatrix(this.tabstate.get('alignment').rows, this.slice.start(), this.slice.end())
            );
            const summaryIdentityValues = movingAverage(identityValues, layout.slidingWindowSize);
            return (
              <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
                <TooltipBarGraph
                  values={summaryIdentityValues}
                  maxValue={1}
                  xFn={(i) => this.slice.get('x')(i + this.slice.start())}
                  maxHeight={layout.height}
                  baselineProps={{stroke: 'black'}}
                />
              </ComponentWrapper>
            );
          },
        }),
      sequenceAxis: () => {
        // This only applies to sequence alignments: safe to assume model is a (Sequence)AlignmentTabState
        const axisView = new SequenceAxisView({
          model: this.tabstate.getSubmapstates()[0],
          tabstate: this.tabstate,
          slice: this.slice,
          parent: this,
        });
        axisView.shouldShow = () => true;
        return axisView;
      },
      alignmentComments: () => {
        // This only applies to sequence alignments: safe to assume model is a (Sequence)AlignmentTabState
        const commentsView = new AlignmentCommentsView({
          model: this.model,
          tabstate: this.tabstate,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
        });
        commentsView.shouldShow = () => true;
        return commentsView;
      },
    };

    this.listComponents = []; // the list of components that are currently rendered
    this.setLineHeights();
  }

  registerGlobalMouseEvents() {
    this.unregisterGlobalMouseEvents();
    $(document).on(`mousemove.summary-${this.cid}`, (event) => {
      this.moveRangeUpdate(event);
    });
    $(document).on(`mouseup.summary-${this.cid}`, () => {
      this.finishRangeUpdate();
    });
  }

  unregisterGlobalMouseEvents() {
    $(document).off(`mousemove.summary-${this.cid}`);
    $(document).off(`mouseup.summary-${this.cid}`);
  }

  // TODO: Remove. height is purely a function of # of rows in alignment. If we do use a layout model,
  //       it could be at least be handled by the tabstate.
  setLineHeights() {
    const numSlices = this.tabstate.getSubmapstates().length;
    const height = Math.max(Math.round(100 / (numSlices + 2)), 10);
    const vMargin = Math.round((100 - height * numSlices) / numSlices);
    this.tabstate.setSummaryMapLineLayout({height, vMargin});
  }

  renderComponentsList() {
    // Remove any existing views:
    this.listComponents.forEach((component) => {
      this.stopListening(component);
      component.remove();
    });
    this.listComponents = [];

    // Iterate through this.y and instantiate the views.
    const masterCid = this.tabstate.getMasterSubmapstateCid();
    this.y.forEach((componentName) => {
      // eslint-disable-next-line benchlint/no-in-operator
      if (componentName in this.componentMethods) {
        const componentMethod = this.componentMethods[componentName];
        this.listComponents.push(componentMethod());
      }
      // eslint-disable-next-line benchlint/no-in-operator
      else if (componentName in this.rowComponentMethods) {
        this.tabstate.getSubmapstates().forEach((mapstate) => {
          const mapStateCid = this.tabstate.getCidForSubmapstate(mapstate);
          // Only render components for top if this.top; render all but top if !this.top.
          if ((masterCid === mapStateCid) !== this.top) {
            return;
          }
          const rowComponentMethod = this.rowComponentMethods[componentName];
          this.listComponents.push(rowComponentMethod(mapstate));
        });
      }
    });

    // Render each view and listen for redraws so we can re-layout the views.
    this.forEachVisibleView((view) => {
      this.listenTo(view, 'redraw', this.adjustAndRange);
      const g = this.sublineLayer.append('svg:g').node();
      view.setElement(g);
      view.render(false);
    });
  }

  reorder() {
    this.renderComponentsList();
    this.adjustHeight();
  }

  rerenderForMapRemoval() {
    this.renderComponentsList();
    this.lazyStart = Math.max(this.lazyStart, 0);
    this.lazyEnd = Math.min(this.lazyEnd, this.listComponents.length);
    this.adjustHeight();
  }

  registerListeners() {
    this.stopListening();
    this.listenTo(this.tabstate, 'change:polymerLayout', this.update);
    this.listenTo(this.slice, 'change', this.update);
    this.listenTo(this.range, 'change', this.doRange);

    // For sequence alignments, deleting rows and re-ordering require manual re-render.
    if (this.model instanceof SummaryMapState) {
      this.listenTo(this.model.get('submapstates'), 'sort', this.reorder);
      this.listenTo(this.model.get('submapstates'), 'remove', (mapstate) => {
        this.rerenderForMapRemoval();
      });
    }
  }

  createRangeRect(root, x, visible = true) {
    let height = this.boxHeight();
    height = Math.max(height, 30);
    root
      .attr('x', (d) => x(d.start))
      .attr('y', 0)
      .attr('width', (d) => x(d.end) - x(d.start))
      .attr('height', (d) => height)
      .attr('fill-opacity', !visible ? 0 : 0.5)
      .classed('range-preview', (d) => d.type === 'preview');

    root.exit().remove();
  }

  renderRange() {
    const data = [];

    data.push({
      start: this.range.start(),
      end: this.range.end(),
      type: 'range',
    });

    if (this.range.get('active')) {
      const rangePreview = this.range.preview().correct();
      data.push({
        start: rangePreview.start(),
        end: rangePreview.end(),
        type: 'preview',
      });
    }

    let visible = this.lineLayer.selectAll('rect.range').data(data);
    const hidden = this.lineLayer.selectAll('rect.range-hidden').data(data);

    const visibleEnter = visible.enter().insert('svg:rect', '.alignmentComments').classed('range', true);
    visible = visible.merge(visibleEnter);

    const {range, slice} = this;

    const drag = d3
      .drag()
      .subject((d) => ({
        x: slice.x()(range.start()), // Don't store slice.x to avoid closuring an old value of slice.x
        y: 0,
      }))
      .on('drag', (e, d, i) => {
        const xInv = slice.get('xInv');
        range.panStart(Math.floor(xInv(e.x)));
      })
      .on('start', (e) => {
        this.lineLayer.selectAll('.range-hidden').classed('grabbed', true);
        e.sourceEvent.stopPropagation();
      })
      .on('end', () => {
        this.lineLayer.selectAll('.range-hidden').classed('grabbed', false);
      });

    hidden
      .enter()
      .insert('svg:rect', '.alignmentComments')
      .classed('range-hidden', true)
      .call(drag)
      .on('mousedown', (e) => {
        // Prevent updateRange from also handling the mousedown event.
        e.stopPropagation();
      });

    const xFn = this.slice.x();
    this.createRangeRect(visible, xFn, true);
    this.createRangeRect(hidden, xFn, false);

    visible.exit().remove();
    hidden.exit().remove();
  }

  die() {
    this.stopListening();
    $(document).off('.summary');
    // eslint-disable-next-line no-restricted-syntax
    _.each(this.listComponents, (view, className, list) => {
      view.die();
    });
  }

  getTop() {
    return this.el.offsetTop - 10;
  }

  finishRangeUpdate() {
    this.lineLayer.selectAll('.range-hidden').classed('grabbed', false);
    if (this.range.get('active')) {
      this.range.correct();
      analytics.track('alignment', 'resized_summary_view_range', {
        start: this.range.start(),
        end: this.range.end(),
        span: this.range.span(),
      });
    }
    this.range.set('active', false);
    this.range.set('dragging', false);
  }

  moveRangeUpdate(e) {
    const offset = e.pageX - this.$el.offset().left;
    const x = Math.round(this.slice.xInv()(offset));
    if (this.range.get('active')) {
      this.range.extend(x);
    }
    if (this.range.get('dragging')) {
      this.range.pan(x);
    }
  }

  updateRange(event) {
    event.preventDefault();
    const offset = event.pageX - this.$el.offset().left;
    const x = Math.round(this.slice.xInv()(offset));
    switch (event.type) {
      case 'mousemove':
        this.moveRangeUpdate(event);
        break;
      case 'mousedown':
        this.lineLayer.selectAll('.range-hidden').classed('grabbed', true);
        this.range.pan(x);
        this.range.set('dragging', true);
        this.registerGlobalMouseEvents();
        event.stopPropagation();
        break;
      case 'mouseup':
        if (this.range.get('dragging') || this.range.get('active')) {
          this.finishRangeUpdate();
          this.unregisterGlobalMouseEvents();
        }
        break;
      case 'dblclick':
        // range.set(start: 0, end: @sequence.length())
        event.stopPropagation();
        break;
      default:
        break;
    }
  }

  doRange() {
    Perf.markStart(this.performanceId);

    this.renderRange();
    this.rangeLines();

    Perf.markEnd(this.performanceId);
  }

  rangeLines() {
    const x = this.slice.x();
    const data = [];
    data.push({
      x: this.range.start(),
      type: 'start',
    });

    data.push({
      x: this.range.end(),
      type: 'end',
    });

    const hide = false;
    const {range} = this;
    const boxHeight = this.boxHeight();

    this.lineLayer
      .selectAll('.range-line')
      .data(data)
      .join('svg:line')
      .classed('range-line', true)
      .attr('x1', (d) => x(d.x))
      .attr('y1', 0)
      .attr('x2', (d) => x(d.x))
      .attr('y2', boxHeight)
      .attr('style', 'cursor:ew-resize')
      .attr('stroke', 'black')
      .attr('stroke-width', 2)
      .classed('hide', hide)
      .on('mousedown', (e, d) => {
        e.stopPropagation();
        range.set({active: true, freeEnd: d.type});
      });
  }

  boxHeight(index) {
    let extra = 0;
    if (index == null) {
      index = this.listComponents.length;
      extra = this.marginBottom;
    }
    return this.listComponents
      .slice(0, index)
      .reduce((total, component) => total + component.boxHeight(), extra);
  }

  adjustHeight() {
    this.forEachVisibleView((component, index) => {
      d3.select(component.el).attr('transform', `translate(0,${this.boxHeight(index)})`);
    });
    const boxHeight = this.boxHeight();
    d3.select(this.el).attr('height', boxHeight);
    this.lineLayer.select('rect.action-rect').attr('height', boxHeight);
    this.triggerHeightChange(boxHeight);
  }

  update() {
    this.forEachVisibleView((view) => {
      view.update(false);
    });
    this.adjustAndRange();
  }

  adjustAndRange() {
    this.adjustHeight();
    this.doRange();
  }

  setWidth(width) {
    d3.select(this.el).attr('width', width);
    if (this.lineLayer) {
      this.lineLayer.select('rect.action-rect').attr('width', width);
    }
  }

  forEachVisibleView(fn) {
    for (let i = 0; i < this.listComponents.length; i++) {
      const view = this.listComponents[i];
      fn(view, i);
    }
  }

  render() {
    this.registerListeners();

    // sublineLayer is appended before lineLayer so it has a lower z-index. sublineLayer is where all of the
    // row views are rendered, while lineLayer is where the selection / range views are rendered, so that
    // they are rendered above the row views. NB: lineLayer is a bit of a misnomer, since the LineViews
    // are actually rendered into the sublineLayer.
    this.sublineLayer = d3.select(this.el).append('svg:g');
    this.lineLayer = d3.select(this.el).append('svg:g');
    this.lineLayer
      .append('svg:rect')
      .classed('action-rect', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', d3.select(this.el).attr('width'))
      .attr('height', 0)
      .attr('fill', 'blue')
      .attr('fill-opacity', 0);

    this.renderComponentsList();
    this.adjustAndRange();

    $(this.el).on(`${MouseWheelEvent}.summary`, (e) => {
      let rx;
      if (e.originalEvent.detailX != null) {
        rx = e.originalEvent.detailX;
      } else if (e.originalEvent.wheelDeltaX != null) {
        rx = e.originalEvent.wheelDeltaX / 16;
      } else if (e.originalEvent.detail != null) {
        rx = -e.originalEvent.detail;
      } else {
        rx = 0;
      }
      let ry;
      if (e.originalEvent.detailY != null) {
        ry = e.originalEvent.detailY;
      } else if (e.originalEvent.wheelDeltaY != null) {
        ry = e.originalEvent.wheelDeltaY / 16;
      } else if (e.originalEvent.detail != null) {
        ry = -e.originalEvent.detail;
      } else {
        ry = 0;
      }
      if (Math.abs(rx) > Math.abs(ry)) {
        this.range.panStart(this.range.start() - rx);
        e.preventDefault();
      }
    });
    return this;
  }
}
SummarySliceView.initClass();
